import { useMemo, useState, useRef, useCallback } from 'react';
import useObservable from 'react-use/lib/useObservable';
import { useApi, appThemeApiRef } from '@backstage/core-plugin-api';

// Delay before showing extended tooltip with "More themes" link
const EXTENDED_TOOLTIP_DELAY = 2000;

export const useThemeToggle = () => {
  const appThemeApi = useApi(appThemeApiRef);
  const themeId = useObservable(
    appThemeApi.activeThemeId$(),
    appThemeApi.getActiveThemeId(),
  );

  const installedThemes = appThemeApi.getInstalledThemes();

  const currentTheme = useMemo(
    () => installedThemes.find(t => t.id === themeId),
    [installedThemes, themeId],
  );

  const isDark = currentTheme?.variant === 'dark';

  // State for extended tooltip (appears after 1.5s hover)
  const [showExtendedTooltip, setShowExtendedTooltip] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isHoveringRef = useRef(false);

  const handleMouseEnter = useCallback(() => {
    isHoveringRef.current = true;
    timerRef.current = setTimeout(() => {
      // Only show tooltip if still hovering when timer fires
      if (isHoveringRef.current) {
        setShowExtendedTooltip(true);
      }
    }, EXTENDED_TOOLTIP_DELAY);
  }, []);

  const handleMouseLeave = useCallback(() => {
    isHoveringRef.current = false;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setShowExtendedTooltip(false);
  }, []);

  const setThemeVariant = useCallback(
    (variant: 'light' | 'dark') => {
      // Reset hover state and clear timer when changing theme
      isHoveringRef.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setShowExtendedTooltip(false);

      // Set to specific Aura theme IDs
      const targetThemeId = variant === 'light' ? 'aura-light' : 'aura-dark';
      const targetTheme = installedThemes.find(t => t.id === targetThemeId);

      if (targetTheme) {
        appThemeApi.setActiveThemeId(targetTheme.id);
      }
    },
    [appThemeApi, installedThemes],
  );

  // Handlers for the "More themes" popper to stay open when hovering over it
  const handlePopperMouseEnter = useCallback(() => {
    // Keep the extended tooltip open
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handlePopperMouseLeave = useCallback(() => {
    setShowExtendedTooltip(false);
  }, []);

  // Ref for anchoring the "More themes" Popper
  const anchorRef = useRef<HTMLDivElement>(null);

  return {
    isDark,
    setThemeVariant,
    showExtendedTooltip,
    handleMouseEnter,
    handleMouseLeave,
    handlePopperMouseEnter,
    handlePopperMouseLeave,
    anchorRef,
  };
};
